// In-memory fingerprint cache avoids re-hashing files already seen this session.
import { showWebCryptoUnavailableToast } from "../browserCapabilityToasts";
import { getBytes, putBytes } from "../idb";

const fileHashCache = new Map<string, string>();

export function clearFileHashCache(): void {
  fileHashCache.clear();
}

// Tracks blob URLs created as IDB/hash fallbacks so scene teardown can revoke them.
const fallbackBlobUrls = new Set<string>();

export function clearFallbackBlobUrls(): void {
  fallbackBlobUrls.forEach((url) => {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  });
  fallbackBlobUrls.clear();
}

export function createFallbackBlobUrl(file: File): string {
  const url = URL.createObjectURL(file);
  fallbackBlobUrls.add(url);
  return url;
}

export async function hashFile(file: File): Promise<string> {
  const cacheKey = `${file.name}:${file.size}`;
  const cached = fileHashCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const buffer = await file.arrayBuffer();
  if (!globalThis.crypto?.subtle) {
    showWebCryptoUnavailableToast("Scene asset import");
    throw new Error("Scene asset import requires Web Crypto hashing.");
  }
  const digest = await globalThis.crypto.subtle.digest("SHA-256", buffer);
  const hex = Array.from(new Uint8Array(digest))
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("");
  fileHashCache.set(cacheKey, hex);
  return hex;
}

export function getCachedBlobByHash(hash: string): Promise<Blob | null> {
  return getBytes(hash.toLowerCase());
}

export function cacheBlobByHash(hash: string, blob: Blob): Promise<void> {
  return putBytes(hash.toLowerCase(), blob);
}
